import React, { useState, useEffect } from 'react';
import styles from './AdminModals.module.css';
import { Project } from '../../types';
import { Modal } from '../../components/common/Modal/Modal';
import { Button } from '../../components/common/Button/Button';
import { projectsAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

interface ProjectManagementModalProps {
  project: Project | null;
  companies: any[];
  users?: any[];
  onSave: (projectData: any) => void;
  onClose: () => void;
}

export const ProjectManagementModal: React.FC<ProjectManagementModalProps> = ({
  project,
  companies,
  users = [],
  onSave,
  onClose
}) => {
  const toast = useToast();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    location: {
      address: '',
      city: '',
      province: '',
      postalCode: ''
    },
    startDate: '',
    endDate: '',
    status: 'planning',
    company: '',
    modules: ['dashboard', 'sentiment', 'personas', 'strategy', 'media', 'werkplanning', 'vragen', 'relatiebeheer'],
    headerImage: ''
  });
  const [headerImageFile, setHeaderImageFile] = useState<File | null>(null);
  const [headerImagePreview, setHeaderImagePreview] = useState<string | null>(null);
  const [uploadingHeaderImage, setUploadingHeaderImage] = useState(false);
  const [projectDocuments, setProjectDocuments] = useState<File[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  useEffect(() => {
    if (project) {
      setFormData({
        name: project.name,
        description: project.description || '',
        location: typeof project.location === 'string'
          ? { address: '', city: project.location, province: '', postalCode: '' }
          : (project as any).location || { address: '', city: '', province: '', postalCode: '' },
        startDate: project.startDate ? new Date(project.startDate).toISOString().split('T')[0] : '',
        endDate: project.endDate ? new Date(project.endDate).toISOString().split('T')[0] : '',
        status: project.status as string || 'planning',
        company: (project as any).company || '',
        modules: (project as any).modules || ['dashboard', 'sentiment', 'personas', 'strategy', 'media', 'werkplanning', 'vragen', 'relatiebeheer'],
        headerImage: (project as any).headerImage || ''
      });

      // Load existing header image preview if it exists
      if ((project as any).headerImage) {
        setHeaderImagePreview((project as any).headerImage);
      } else {
        setHeaderImagePreview(null);
      }

      // Clear the file input when loading a project
      setHeaderImageFile(null);

      // Load assigned users
      const projectId = project.id || (project as any)._id;
      const assignedIds = users
        .filter((u: any) => u.projects?.some((p: any) => (p._id || p) === projectId))
        .map((u: any) => u._id || u.id);
      setSelectedUserIds(assignedIds);
    }
  }, [project, users]);

  const handleHeaderImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.warning('Afbeelding te groot', 'De header afbeelding mag maximaal 10MB zijn.');
        return;
      }

      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        toast.warning('Ongeldig formaat', 'Upload een geldig afbeeldingsbestand (JPEG, PNG, GIF, SVG of WebP).');
        return;
      }

      setHeaderImageFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setHeaderImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveHeaderImage = async () => {
    // If editing an existing project with a header image, delete it from the server
    const projectId = project?.id || (project as any)?._id;
    if (projectId && (project as any).headerImage) {
      if (window.confirm('Are you sure you want to remove the header image?')) {
        try {
          await projectsAPI.deleteHeaderImage(projectId);
          setHeaderImagePreview(null);
          setHeaderImageFile(null);
          setFormData({ ...formData, headerImage: '' });
          toast.success('Afbeelding verwijderd', 'De header afbeelding is verwijderd.');
        } catch (error) {
          console.error('Failed to delete header image:', error);
          toast.error('Verwijderen mislukt', 'De header afbeelding kon niet worden verwijderd.');
        }
      }
    } else {
      // Just removing the preview for a new upload
      setHeaderImageFile(null);
      setHeaderImagePreview(null);
      setFormData({ ...formData, headerImage: '' });
    }
  };

  const handleDocumentChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const validFiles: File[] = [];
    const validTypes = ['.pdf', '.doc', '.docx', '.txt'];

    // Validate each file
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // Validate file size (max 50MB per file)
      if (file.size > 50 * 1024 * 1024) {
        toast.warning('Bestand te groot', `"${file.name}" is groter dan 50MB.`);
        continue;
      }

      // Validate file type
      const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      if (!validTypes.includes(ext)) {
        toast.warning('Ongeldig formaat', `"${file.name}" wordt niet ondersteund. Upload PDF, DOC, DOCX of TXT bestanden.`);
        continue;
      }

      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    // Add to existing documents
    setProjectDocuments(prev => [...prev, ...validFiles]);

    // Note: We'll analyze documents when the form is submitted,
    // not immediately after upload, to handle multiple documents efficiently
  };

  const handleRemoveDocument = (index: number) => {
    setProjectDocuments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // If there are documents to analyze, use the new endpoint
      if (!project && projectDocuments.length > 0) {
        // Show loading message
        setUploadingHeaderImage(true);

        const response = await projectsAPI.createWithDocuments(formData, projectDocuments);
        const createdProjectId = response.project._id || response.project.id;

        // Upload header image if provided
        if (headerImageFile && createdProjectId) {
          try {
            await projectsAPI.uploadHeaderImage(createdProjectId, headerImageFile);
          } catch (error) {
            console.error('Failed to upload header image:', error);
          }
        }

        // Show success message with import summary if documents were analyzed
        if (response.documentAnalysis?.imported) {
          const summary = response.documentAnalysis.summary;
          const details = [
            { label: "Persona's", value: summary.personasImported },
            { label: 'Strategieën', value: summary.strategiesImported },
            { label: 'Werkplanningen', value: summary.workPlanningsImported },
          ];
          if (summary.coostoTagsAdded > 0) {
            details.push({ label: 'Coosto tags', value: summary.coostoTagsAdded });
            if (summary.coostoSyncStatus === 'success') {
              details.push({ label: 'Coosto berichten', value: summary.coostoMessagesFound });
            }
          }
          toast.showToast('success', {
            title: 'Project aangemaakt',
            message: `Data geïmporteerd uit ${projectDocuments.length} document(en).`,
            details,
            duration: 10000
          });
        } else if (response.documentAnalysis?.error) {
          toast.warning('Project aangemaakt', `Documentanalyse is mislukt: ${response.documentAnalysis.error}`);
        } else {
          toast.success('Project aangemaakt');
        }

        onClose();
        window.location.reload(); // Refresh to show the new project
        return;
      }

      // Otherwise use regular save (create or update project)
      // Remove headerImage from formData - images are handled separately via upload endpoint
      const { headerImage, ...projectDataWithoutImage } = formData;
      await onSave({ ...projectDataWithoutImage, teamUserIds: selectedUserIds });

      // Upload header image if there's a new one and we're editing an existing project
      const projectId = project?.id || (project as any)?._id;
      if (headerImageFile && projectId) {
        setUploadingHeaderImage(true);
        try {
          const headerResponse = await projectsAPI.uploadHeaderImage(projectId, headerImageFile);
          console.log('Header image uploaded:', headerResponse.headerImageUrl);
        } catch (error) {
          console.error('Failed to upload header image:', error);
          toast.warning('Afbeelding upload mislukt', 'Het project is opgeslagen, maar de header afbeelding kon niet worden geüpload.');
        } finally {
          setUploadingHeaderImage(false);
        }
      } else if (headerImageFile && !project) {
        // For new projects without documents, user will need to edit and add header image after creation
        toast.info('Project aangemaakt', 'Bewerk het project om een header afbeelding toe te voegen.');
      }
    } catch (error) {
      console.error('Failed to save project:', error);
    }
  };

  const handleModuleToggle = (module: string) => {
    setFormData(prev => ({
      ...prev,
      modules: prev.modules.includes(module)
        ? prev.modules.filter(m => m !== module)
        : [...prev.modules, module]
    }));
  };

  const availableModules = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'sentiment', label: 'Sentiment Analysis' },
    { id: 'personas', label: 'Personas' },
    { id: 'strategy', label: 'Strategy' },
    { id: 'media', label: 'Media' },
    { id: 'werkplanning', label: 'Work Planning' },
    { id: 'vragen', label: 'Questions' },
    { id: 'relatiebeheer', label: 'Relationship Management' }
  ];

  return (
    <Modal isOpen={true} onClose={onClose} title={project ? 'Edit Project' : 'Create New Project'}>
      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formGroup}>
          <label htmlFor="name">Project Name *</label>
          <input
            type="text"
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={3}
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="headerImage">Project Header Image</label>
          <div className={styles.logoUploadContainer}>
            {headerImagePreview ? (
              <div className={styles.logoPreview} style={{ width: '100%', height: '200px' }}>
                <img src={headerImagePreview} alt="Header preview" style={{ objectFit: 'cover' }} />
                <button
                  type="button"
                  onClick={handleRemoveHeaderImage}
                  className={styles.removeLogoBtn}
                  title="Remove header image"
                >
                  ×
                </button>
              </div>
            ) : (
              <div className={styles.logoPlaceholder} style={{ width: '100%', height: '200px' }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9DA6A8" strokeWidth="1.5">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </svg>
                <span>No header image uploaded</span>
              </div>
            )}
            <input
              type="file"
              id="headerImage"
              accept="image/*"
              onChange={handleHeaderImageChange}
              className={styles.fileInput}
            />
            <label htmlFor="headerImage" className={styles.uploadButton}>
              Choose Header Image
            </label>
            {headerImageFile && (
              <span className={styles.fileName}>{headerImageFile.name}</span>
            )}
          </div>
          <small>Maximum file size: 10MB. Supported formats: JPEG, PNG, GIF, SVG, WebP. Recommended size: 1920x400px</small>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="city">City *</label>
            <input
              type="text"
              id="city"
              value={formData.location.city}
              onChange={(e) => setFormData({
                ...formData,
                location: { ...formData.location, city: e.target.value }
              })}
              required
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="province">Province</label>
            <input
              type="text"
              id="province"
              value={formData.location.province}
              onChange={(e) => setFormData({
                ...formData,
                location: { ...formData.location, province: e.target.value }
              })}
            />
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="startDate">Start Date</label>
            <input
              type="date"
              id="startDate"
              value={formData.startDate}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="endDate">End Date</label>
            <input
              type="date"
              id="endDate"
              value={formData.endDate}
              onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
            />
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="status">Status *</label>
            <select
              id="status"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              required
            >
              <option value="planning">Planning</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="company">Company</label>
            <select
              id="company"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
            >
              <option value="">Select Company</option>
              {companies.map((company) => (
                <option key={company._id} value={company._id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {users.length > 0 && (
          <div className={styles.formGroup}>
            <label>Teamleden</label>
            <div style={{
              maxHeight: '160px', overflowY: 'auto', border: '1px solid #DDDDDD',
              borderRadius: '6px', padding: '0.5rem',
            }}>
              {(formData.company
                ? users.filter((u: any) => (u.company?._id || u.company) === formData.company)
                : users
              ).map((user: any) => (
                <label
                  key={user._id || user.id}
                  className={styles.checkboxLabel}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.375rem 0.25rem' }}
                >
                  <input
                    type="checkbox"
                    checked={selectedUserIds.includes(user._id || user.id)}
                    onChange={() => {
                      const uid = user._id || user.id;
                      setSelectedUserIds(prev =>
                        prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]
                      );
                    }}
                    style={{ accentColor: '#E6007E' }}
                  />
                  <span>{user.firstName} {user.lastName}</span>
                  <span style={{ color: '#9DA6A8', fontSize: '0.8rem', marginLeft: 'auto' }}>{user.email}</span>
                </label>
              ))}
            </div>
            <small>{selectedUserIds.length} teamleden geselecteerd</small>
          </div>
        )}

        {!project && (
          <div className={styles.formGroup}>
            <label htmlFor="projectDocuments">Project Documents (Optional)</label>
            <div className={styles.documentUploadContainer}>
              {projectDocuments.length > 0 ? (
                <div className={styles.documentsList}>
                  {projectDocuments.map((doc, index) => (
                    <div key={index} className={styles.documentItem}>
                      <div className={styles.documentName}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#E6007E" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                        </svg>
                        <span>{doc.name}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveDocument(index)}
                          className={styles.removeDocBtn}
                          title="Remove document"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.documentPlaceholder}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#9DA6A8" strokeWidth="1.5">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>
                  <span>Upload project documents to auto-populate personas, strategies, and work planning</span>
                </div>
              )}
              <input
                type="file"
                id="projectDocuments"
                accept=".pdf,.doc,.docx,.txt"
                onChange={handleDocumentChange}
                className={styles.fileInput}
                multiple
              />
              <label htmlFor="projectDocuments" className={styles.uploadButton}>
                {projectDocuments.length > 0 ? 'Add More Documents' : 'Choose Documents'}
              </label>
              {projectDocuments.length > 0 && (
                <span className={styles.documentsCount}>{projectDocuments.length} document(s) selected</span>
              )}
            </div>
            <small>Upload project documents (PDF, DOC, DOCX, or TXT) and AI will extract stakeholder personas, communication strategies, and work planning automatically. Maximum file size: 50MB per file. You can upload multiple documents.</small>
          </div>
        )}

        <div className={styles.formGroup}>
          <label>Active Modules</label>
          <div className={styles.modulesGrid}>
            {availableModules.map((module) => (
              <label key={module.id} className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={formData.modules.includes(module.id)}
                  onChange={() => handleModuleToggle(module.id)}
                />
                <span>{module.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className={styles.formActions}>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={uploadingHeaderImage}>
            {uploadingHeaderImage ? 'Uploading...' : (project ? 'Update Project' : 'Create Project')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};